import type React from "react";
import { useEffect, useRef, useState } from "react";

type Props = {
	className?: string;
	alt?: string;
	fallbackSrc: string;
	initialSrc?: string | null;
	refreshKey?: number;
	fetchSrc: (signal?: AbortSignal) => Promise<string | null | undefined>;
	onLoadingChange?: (loading: boolean) => void;
};

export const AsyncLogoImage: React.FC<Props> = ({
	className = "",
	alt = "Logo preview",
	fallbackSrc,
	initialSrc,
	refreshKey,
	fetchSrc,
	onLoadingChange,
}) => {
	const [src, setSrc] = useState<string | null>(initialSrc ?? null);
	const [loading, setLoading] = useState(true);
	const objectUrlRef = useRef<string | null>(null);

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();

		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}

		setLoading(true);
		onLoadingChange?.(true);
		setSrc(initialSrc ?? null);

		fetchSrc(controller.signal)
			.then(async (url) => {
				if (!isMounted) return;
				if (!url) {
					setSrc(fallbackSrc);
					return;
				}

				try {
					const response = await fetch(url, {
						cache: "no-store",
						signal: controller.signal,
					});

					if (!response.ok) {
						setSrc(fallbackSrc);
						return;
					}

					const blob = await response.blob();
					if (!isMounted) return;

					const objectUrl = URL.createObjectURL(blob);
					objectUrlRef.current = objectUrl;
					setSrc(objectUrl);
				} catch {
					if (!isMounted) return;
					setSrc(fallbackSrc);
				}
			})
			.catch(() => {
				if (!isMounted) return;
				setSrc(fallbackSrc);
			})
			.finally(() => {
				if (!isMounted) return;
				setLoading(false);
				onLoadingChange?.(false);
			});

		return () => {
			isMounted = false;
			controller.abort();
			if (objectUrlRef.current) {
				URL.revokeObjectURL(objectUrlRef.current);
				objectUrlRef.current = null;
			}
		};
	}, [fallbackSrc, initialSrc, fetchSrc, refreshKey, onLoadingChange]);

	const handleError = () => {
		setSrc(fallbackSrc);
		setLoading(false);
		onLoadingChange?.(false);
	};

	if (loading) {
		return (
			<div
				className={`w-full h-full bg-gpa-gray-100 animate-pulse ${className}`}
			/>
		);
	}

	return (
		<img
			src={src || fallbackSrc}
			alt={alt}
			className={className}
			loading="eager"
			{...({
				fetchPriority: "high",
			} as React.ImgHTMLAttributes<HTMLImageElement>)}
			onError={handleError}
			onErrorCapture={handleError}
		/>
	);
};
